// Obsah feature podstránek (/funkce/:slug). Každá má ~A4 textu pro profesionální dojem i SEO.

export interface FeatureSection {
  h: string
  p?: string[]
  ul?: string[]
}

export interface FeaturePageData {
  slug: string
  tag: string
  title: string
  subtitle: string
  sections: FeatureSection[]
  ctaTitle: string
  ctaText: string
  ctaButton: string
  ctaLink: string
}

const CTA_ANALYZE = { ctaTitle: 'Vyzkoušejte to na svém webu', ctaText: 'Zadejte adresu a získejte skóre i konkrétní doporučení během pár sekund.', ctaButton: 'Spustit analýzu zdarma', ctaLink: '/' }
const CTA_TOOLS = { ctaTitle: 'Otevřete SEO nástroje', ctaText: 'Všech 24 nástrojů na jednom místě — vyberte si, co potřebujete prověřit.', ctaButton: 'Přejít na nástroje', ctaLink: '/tools' }
const CTA_REGISTER = { ctaTitle: 'Odemkněte tuto funkci', ctaText: 'Vytvořte si účet zdarma a vyzkoušejte pokročilé funkce. Stačí pár sekund a žádná karta.', ctaButton: 'Vytvořit účet zdarma', ctaLink: '/register' }

export const FEATURE_PAGES: FeaturePageData[] = [
  {
    slug: 'bezpecnost-https',
    tag: 'Bezpečnost & HTTPS',
    title: 'Bezpečnost webu a HTTPS',
    subtitle: 'SSL certifikát, bezpečnostní hlavičky, cookies a GDPR compliance — vše, co dnes ovlivňuje důvěru návštěvníků i hodnocení vyhledávačů.',
    sections: [
      { h: 'Proč na bezpečnosti záleží', p: [
        'Zabezpečení webu už dávno není jen technický detail. Google od roku 2018 označuje weby bez HTTPS jako „nezabezpečené", prohlížeče u nich zobrazují varování a část návštěvníků takový web rovnou opustí. Šifrované spojení (SSL/TLS) je dnes naprostý základ — chrání data, která mezi návštěvníkem a serverem putují, a je jedním z potvrzených ranking faktorů.',
        'Nad rámec samotného certifikátu rozhoduje o reálné odolnosti webu sada bezpečnostních hlaviček. Ty určují, jak se má prohlížeč chovat — zda smí web vložit do rámu (clickjacking), jak striktně vynucovat HTTPS, nebo z jakých zdrojů smí načítat skripty a styly.',
      ] },
      { h: 'Co v této oblasti kontrolujeme', ul: [
        'Platnost a správné nasazení SSL/TLS certifikátu a vynucení HTTPS (přesměrování z HTTP).',
        'HSTS (Strict-Transport-Security) — vynucení šifrovaného spojení na úrovni prohlížeče.',
        'X-Frame-Options a Content-Security-Policy — ochrana proti clickjackingu a injektáži kódu.',
        'X-Content-Type-Options, Referrer-Policy a Permissions-Policy.',
        'Cookies a GDPR — přítomnost cookie consent banneru, odkaz na zásady ochrany osobních údajů a bezpečné nastavení cookies (Secure, HttpOnly, SameSite).',
      ] },
      { h: 'Jak to v SEO Tools funguje', p: [
        'Nástroj načte hlavičky odpovědi serveru i samotnou stránku a vyhodnotí každý bod zvlášť — od certifikátu přes jednotlivé hlavičky až po cookies. U každého zjištění vidíte stav (v pořádku / varování / problém), vysvětlení proč na něm záleží a konkrétní postup, jak ho napravit, často včetně ukázky konfigurace pro Apache nebo Nginx.',
        'Bezpečnostní skóre tak získáte na jedné obrazovce a hned víte, kde má web slabé místo a jak ho během chvíle uzavřít.',
      ] },
    ],
    ...CTA_ANALYZE,
  },
  {
    slug: 'on-page-seo',
    tag: 'On-page SEO',
    title: 'On-page SEO analýza',
    subtitle: 'Title, meta description, nadpisy H1–H6, obrázky, canonical, Open Graph i Twitter Cards — kompletní kontrola prvků, které rozhodují o pozici stránky.',
    sections: [
      { h: 'Co je on-page SEO', p: [
        'On-page SEO zahrnuje všechno, co máte přímo pod kontrolou na vlastní stránce — od titulku a popisku přes strukturu nadpisů až po strukturovaná data. Právě tyto prvky vyhledávač čte jako první a podle nich rozhoduje, o čem stránka je a jak ji zařadit do výsledků.',
        'Drobná pochybení jako chybějící meta description, dva nadpisy H1 nebo obrázky bez alt textu se snadno přehlédnou, ale dohromady dokážou výrazně srazit výkon i jinak kvalitního obsahu.',
      ] },
      { h: 'Co kontrolujeme', ul: [
        'Title tag — délka, unikátnost a přítomnost klíčového slova.',
        'Meta description — délka a relevance pro proklik z výsledků.',
        'Hierarchie nadpisů H1–H6 a její logická struktura.',
        'Obrázky — alt texty, moderní formáty, lazy loading a rozměry.',
        'Canonical URL — prevence duplicitního obsahu.',
        'Open Graph a Twitter Cards pro náhledy při sdílení.',
        'Strukturovaná data (Schema.org / JSON-LD) pro rich snippets.',
        'Deklarace jazyka, viewport a meta robots.',
      ] },
      { h: 'Jak vám to pomůže', p: [
        'SEO Tools projde 18 on-page kontrol a každou ohodnotí. Výsledkem je skóre 0–100 % a seřazený seznam toho, co opravit jako první. U každého bodu najdete vysvětlení i doporučený postup, takže analýzu zvládne i člověk bez hlubokých technických znalostí.',
        'Pravidelnou kontrolou udržíte web v technické kondici a předejdete tomu, aby drobné chyby brzdily jeho viditelnost.',
      ] },
    ],
    ...CTA_ANALYZE,
  },
  {
    slug: 'vykon-rychlost',
    tag: 'Výkon & rychlost',
    title: 'Výkon a rychlost načítání',
    subtitle: 'TTFB, velikost stránky, render-blocking zdroje, komprese, caching a velikost DOM — rychlost je dnes zážitek uživatele i ranking faktor.',
    sections: [
      { h: 'Proč na rychlosti záleží', p: [
        'Rychlost načítání přímo ovlivňuje, kolik návštěvníků na webu zůstane. Studie opakovaně ukazují, že každá vteřina navíc zvyšuje míru opuštění a snižuje konverze. Google navíc rychlost a stabilitu stránky (Core Web Vitals) zohledňuje při hodnocení.',
        'Pomalý web přitom často nebrzdí jedna velká chyba, ale součet drobností — nekomprimované soubory, blokující skripty v hlavičce, chybějící cache nebo zbytečně velký DOM.',
      ] },
      { h: 'Co měříme', ul: [
        'TTFB (Time To First Byte) — jak rychle odpoví server.',
        'Celková velikost stránky a počet načítaných zdrojů (CSS, JS, obrázky).',
        'Render-blocking zdroje, které zdržují vykreslení.',
        'Komprese (gzip / brotli) a její nasazení na textové soubory.',
        'Browser caching a hlavičky Cache-Control.',
        'Velikost a složitost DOM stromu.',
      ] },
      { h: 'Od měření k řešení', p: [
        'U každého zjištění nezůstáváme jen u čísla — ukážeme i odhad úspory a konkrétní krok, jak ji získat: zapnout kompresi, přidat defer na skripty, nastavit dlouhou cache pro statické soubory nebo zmenšit obrázky.',
        'Výsledkem je jasná představa, kde web ztrácí rychlost a co udělat jako první pro největší efekt.',
      ] },
    ],
    ...CTA_ANALYZE,
  },
  {
    slug: 'crawl-webu',
    tag: 'Crawl webu',
    title: 'Crawl celého webu',
    subtitle: 'Projděte všechny stránky webu a na každé proveďte kompletní SEO analýzu. Výstupy v PDF, CSV i jako sitemap.',
    sections: [
      { h: 'Co crawl umí', p: [
        'Jednorázová analýza prověří jednu adresu — crawl ale projde celý web. Postupně následuje odkazy, načítá podstránku po podstránce a na každé spustí stejnou sadu kontrol jako u jednotlivé analýzy. Získáte tak přehled o stavu celého webu, ne jen domovské stránky.',
        'To je klíčové u větších webů a e-shopů, kde se problémy (duplicitní titulky, chybějící popisky, rozbité odkazy) typicky skrývají hluboko ve struktuře.',
      ] },
      { h: 'Co odhalíte', ul: [
        'Duplicitní titulky a meta description napříč webem.',
        'Stránky s chybami, varováními nebo nízkým skóre.',
        'Rozbité interní odkazy a problematická přesměrování.',
        'Stránky blokované pro indexaci nebo bez canonicalu.',
        'Možnost renderovat JavaScript pro SPA a weby s bot-ochranou.',
      ] },
      { h: 'Výstupy a reporty', p: [
        'Výsledky crawlu si můžete prohlédnout v přehledné tabulce s filtrováním, exportovat do PDF reportu pro klienta, stáhnout jako CSV pro další zpracování nebo vygenerovat sitemap. Crawl lze spustit i opakovaně a sledovat, jak se stav webu vyvíjí v čase.',
        'Rozsah crawlu (počet stránek) se řídí vaším plánem, takže si vyberete podle velikosti webu.',
      ] },
    ],
    ...CTA_TOOLS,
  },
  {
    slug: 'sledovani-pozic',
    tag: 'Sledování pozic',
    title: 'Sledování pozic ve vyhledávačích',
    subtitle: 'Google, Seznam. Sledujte klíčová slova v čase, s lokalizací na města, streamingem výsledků a automatickými kontrolami.',
    sections: [
      { h: 'Proč sledovat pozice', p: [
        'Bez měření pozic nevíte, jestli vaše SEO úsilí funguje. Sledování pozic ukazuje, na kterých klíčových slovech se web objevuje, jak se jeho umístění mění a kde je největší potenciál k růstu.',
        'SEO Tools sleduje pozice napříč více vyhledávači a lokalitami, takže máte reálný obraz viditelnosti — ne jen odhad.',
      ] },
      { h: 'Co nabízíme', ul: [
        'Vyhledávače Google CZ, Google COM, Seznam.cz.',
        'Lokalizace na konkrétní města (Praha, Brno, Ostrava a další).',
        'Historie pozic a přehledné grafy vývoje v čase.',
        'Streaming výsledků — pozice se zobrazují průběžně, jak se kontrolují.',
        'Automatické kontroly podle plánu (denně, týdně, měsíčně) a e-mailové upozornění na výrazné změny.',
      ] },
      { h: 'Jak se počítá spotřeba', p: [
        'Reálnou jednotkou je sledovaná pozice = klíčové slovo × vyhledávač × lokace. Pokud sledujete jedno slovo na dvou vyhledávačích a třech městech, jde o šest pozic. Tento přístup odpovídá skutečnému počtu dotazů, takže limity plánu přesně kopírují náklady a vždy víte, kolik pozic vám zbývá.',
        'Sdílená cache výsledků navíc šetří opakované dotazy na stejné klíčové slovo, takže sledování je efektivní.',
      ] },
    ],
    ...CTA_TOOLS,
  },
  {
    slug: 'obsah-klicova-slova',
    tag: 'Obsah & klíčová slova',
    title: 'Analýza obsahu a klíčových slov',
    subtitle: 'Hustota klíčových slov, čitelnost, sémantika nadpisů a bigramy — aby obsah dával smysl lidem i vyhledávačům.',
    sections: [
      { h: 'Obsah je základ', p: [
        'Sebelepší technické SEO nepomůže, pokud obsah neodpovídá tomu, co lidé hledají. Analýza obsahu pomáhá zjistit, zda stránka pokrývá téma do potřebné hloubky, jestli je čitelná a jak pracuje s klíčovými slovy.',
        'Cílem není „nacpat" do textu klíčová slova, ale přirozeně a srozumitelně pokrýt téma — což oceňují čtenáři i moderní vyhledávače včetně AI.',
      ] },
      { h: 'Co analyzujeme', ul: [
        'Počet slov a odhadovaná doba čtení.',
        'Hustota klíčových slov a nejčastější dvojslovné fráze (bigramy).',
        'Čitelnost a členění textu do odstavců.',
        'Sémantická struktura nadpisů a jejich návaznost na obsah.',
        'Detekci hlavního tématu stránky a jeho konzistence s titulkem.',
      ] },
      { h: 'Od analýzy k lepšímu textu', p: [
        'Výstup vám ukáže, na co se obsah reálně zaměřuje, kde je příliš tenký nebo naopak rozmělněný a která témata by stálo za to doplnit. V kombinaci s Content Optimizerem, který rozebírá top výsledky vyhledávání, získáte konkrétní seznam výrazů, jež by váš obsah měl pokrýt, aby předčil konkurenci.',
      ] },
    ],
    ...CTA_ANALYZE,
  },
  {
    slug: 'pristupnost',
    tag: 'Přístupnost',
    title: 'Přístupnost a mobilní optimalizace',
    subtitle: 'WCAG kontroly, mobilní přívětivost, viewport a formuláře — web, který funguje pro každého, je i lépe hodnocený.',
    sections: [
      { h: 'Přístupnost není jen povinnost', p: [
        'Přístupný web mohou používat i lidé s hendikepem — se čtečkami obrazovky, ovládáním z klávesnice nebo se zhoršeným zrakem. Kromě toho, že jde čím dál častěji o zákonný požadavek, přístupnost zlepšuje použitelnost pro všechny a koreluje s kvalitnějším, lépe strukturovaným webem.',
        'Mnoho principů přístupnosti se navíc překrývá s SEO — sémantické nadpisy, alt texty obrázků nebo srozumitelná struktura prospívají lidem i vyhledávačům zároveň.',
      ] },
      { h: 'Co kontrolujeme', ul: [
        'Sémantické prvky stránky — landmarky main, nav, header, footer.',
        'Alt texty obrázků a popisky odkazů a tlačítek.',
        'Kontrast textu, velikost písma a možnost přiblížení (zoom).',
        'Mobilní přívětivost a správné nastavení viewportu.',
        'Přístupnost formulářů — popisky polí a stavy.',
        'Skip-to-content odkaz pro ovládání klávesnicí.',
      ] },
      { h: 'Výsledek', p: [
        'Nástroj projde stránku podle zásad WCAG a mobilní optimalizace a u každého nedostatku navrhne konkrétní úpravu. Postupným odstraňováním zlepšíte zážitek návštěvníků na mobilech i desktopu a zároveň posílíte technický základ pro SEO.',
      ] },
    ],
    ...CTA_ANALYZE,
  },
  {
    slug: 'srovnani-konkurence',
    tag: 'Srovnání konkurence',
    title: 'Srovnání s konkurencí',
    subtitle: 'Porovnejte dva weby vedle sebe — rychlost, SEO, obsah i použité technologie. Zjistěte, kde máte navrch a kde dohánět.',
    sections: [
      { h: 'Proč se srovnávat', p: [
        'SEO se neodehrává ve vakuu — vaše pozice závisí na tom, jak si vedete vůči konkurenci. Přímé srovnání dvou webů odhalí, v čem je konkurent lepší (rychlejší web, lepší struktura, bohatší obsah) a kde naopak můžete získat výhodu.',
        'Místo dohadů tak dostanete konkrétní, měřitelné porovnání, ze kterého se dá vyjít při plánování dalších kroků.',
      ] },
      { h: 'Co porovnáváme', ul: [
        'Celkové SEO skóre obou webů vedle sebe.',
        'Rychlost a výkon — velikost, rychlost odezvy, zdroje.',
        'On-page prvky — title, popisky, nadpisy, strukturovaná data.',
        'Rozsah a kvalitu obsahu.',
        'Použité technologie (CMS, frameworky, analytika).',
      ] },
      { h: 'Jak to využít', p: [
        'Srovnání spustíte zadáním dvou adres — svého webu a konkurenta. Výstup přehledně ukáže rozdíly v jednotlivých oblastech, takže rychle identifikujete své silné stránky i mezery, které stojí za to dohnat.',
        'Pro hlubší analýzu konkurence ve vyhledávání lze využít i sledování pozic a analýzu konkurenčních domén na stejných klíčových slovech.',
      ] },
    ],
    ...CTA_TOOLS,
  },
  {
    slug: 'seo-nastroje',
    tag: '24 SEO nástrojů',
    title: '17 samostatných SEO nástrojů',
    subtitle: 'Robots.txt, sitemap, přesměrování, mrtvé odkazy, DNS/SSL, bulk check a další — každý nástroj řeší jednu věc pořádně.',
    sections: [
      { h: 'Vše na jednom místě', p: [
        'Kromě komplexní analýzy nabízí SEO Tools sadu 17 samostatných nástrojů, které se hodí, když potřebujete rychle prověřit jednu konkrétní věc. Nemusíte přeskakovat mezi pěti různými službami — vše máte pod jednou střechou a v jednotném rozhraní.',
      ] },
      { h: 'Které nástroje to jsou', ul: [
        'Robots.txt a přístup botů (včetně AI botů pro GEO).',
        'XML sitemap — kontrola a výpis URL.',
        'Strukturovaná data a SERP náhled.',
        'Hreflang pro vícejazyčné weby.',
        'Mobilní test, bezpečnostní hlavičky, PageSpeed.',
        'Přesměrování (301/302, HTTP→HTTPS, www) a mrtvé odkazy.',
        'Obsah, technologie, přístupnost, cookies/GDPR.',
        'DNS a SSL, srovnání webů, hromadná kontrola (bulk) a GEO/AI.',
      ] },
      { h: 'Pro koho jsou určené', p: [
        'Nástroje ocení každý, kdo se o web stará pravidelně — od majitelů webů přes marketéry až po agentury. Funkce „Spustit vše" navíc projde najednou většinu nástrojů a sestaví z výsledků jeden komplexní report, který lze exportovat do PDF.',
        'Vyberte si nástroj podle toho, co právě potřebujete vyřešit, nebo nechte proběhnout kompletní kontrolu a získejte celkový obrázek.',
      ] },
    ],
    ...CTA_TOOLS,
  },
  {
    slug: 'ai-viditelnost',
    tag: 'AI Viditelnost',
    title: 'AI Viditelnost — citují vás ChatGPT a Perplexity?',
    subtitle: 'Zjistěte, zda vás generativní vyhledávače (ChatGPT, Gemini, Perplexity, Google AI Overviews) zmiňují jako zdroj — a jak si stojíte proti konkurenci.',
    sections: [
      { h: 'Proč řešit viditelnost v AI', p: [
        'Stále větší část lidí už nehledá jen v klasickém vyhledávači, ale ptá se přímo AI asistentů. ChatGPT, Google Gemini, Perplexity nebo AI Overviews ve výsledcích Googlu odpovídají vlastními slovy a citují přitom jen některé zdroje. Pokud mezi nimi nejste vy, přicházíte o viditelnost, kterou žádný klasický rank tracking nezachytí.',
        'Tahle disciplína se označuje jako GEO (Generative Engine Optimization) a je to nová vrstva SEO. Nejdřív ale musíte vědět, jak na tom jste — jestli vás AI vůbec zná a u kterých dotazů.',
      ] },
      { h: 'Co nástroj umí', ul: [
        'Položí vaše dotazy reálným AI modelům a vyhodnotí, zda zmíní vaši značku nebo doménu.',
        'Skóre viditelnosti a přehled, u kterých promptů jste citováni a u kterých ne.',
        'Porovnání s konkurencí — kdo je v odpovědích AI zmiňován místo vás.',
        'Sledování více AI vyhledávačů (ChatGPT, Gemini, Claude, Perplexity a další).',
        'Projekty s automatickými kontrolami v čase, abyste viděli vývoj.',
      ] },
      { h: 'Jak to funguje', p: [
        'Funkce běží na principu „přines si vlastní klíč" (BYOK) — připojíte svůj API klíč k AI providerům a my přes něj položíme vaše dotazy. Díky tomu testujete na skutečných modelech a platíte jen reálnou spotřebu u providera. Výsledky se ukládají do projektu, takže můžete sledovat, jak se vaše citovatelnost mění poté, co obsah upravíte.',
        'Funkce je dostupná po přihlášení. Vytvoření účtu je zdarma a zabere pár sekund.',
      ] },
    ],
    ...CTA_REGISTER,
  },
  {
    slug: 'zpetne-odkazy',
    tag: 'Zpětné odkazy',
    title: 'Analýza zpětných odkazů',
    subtitle: 'Zjistěte, kdo na vás odkazuje, jak silný je váš odkazový profil a kde máte oproti konkurenci rezervy. Data z globální databáze odkazů.',
    sections: [
      { h: 'Proč na zpětných odkazech záleží', p: [
        'Zpětné odkazy (backlinky) jsou jedním z nejsilnějších faktorů, podle kterých vyhledávače posuzují důvěryhodnost a autoritu webu. Kvalitní odkaz z relevantního webu funguje jako doporučení — čím víc takových doporučení máte, tím lépe se vám daří probojovat na přední pozice.',
        'Stejně důležité je ale poznat i rizikové odkazy. Spamové nebo nepřirozené odkazy mohou web naopak poškodit, a proto je dobré mít o svém odkazovém profilu přehled.',
      ] },
      { h: 'Co v analýze uvidíte', ul: [
        'Celkový počet zpětných odkazů a odkazujících domén.',
        'Autorita domény a profil nejsilnějších odkazujících webů.',
        'Anchor texty — pod jakými frázemi na vás ostatní odkazují.',
        'Poměr follow / nofollow a upozornění na podezřelé vzorce.',
        'Možnost porovnat svůj odkazový profil s konkurencí.',
      ] },
      { h: 'Odkud data pocházejí', p: [
        'Analýza čerpá z rozsáhlé globální databáze odkazů (DataForSEO), takže nejde o pouhý odhad, ale o reálná data o tom, kdo na vás a vaši konkurenci odkazuje. Výsledek vám pomůže najít příležitosti k získání nových odkazů a odhalit slabá místa profilu.',
        'Funkce je dostupná po přihlášení v rámci placených plánů. Účet si vytvoříte zdarma a plán vyberete podle potřeby.',
      ] },
    ],
    ...CTA_REGISTER,
  },
  {
    slug: 'google-search-console',
    tag: 'Google Search Console',
    title: 'Napojení na Google Search Console',
    subtitle: 'Propojte svůj účet GSC a mějte reálná data z Googlu — dotazy, prokliky, zobrazení i pozice — přehledně na jednom místě, s exportem do reportu.',
    sections: [
      { h: 'Proč propojit Search Console', p: [
        'Google Search Console je jediný zdroj skutečných dat o tom, jak váš web vystupuje ve výsledcích Googlu — kolikrát se zobrazil, na jaké dotazy, kolik lidí kliklo a na jaké průměrné pozici. Tato data nikde jinde nezískáte, protože pocházejí přímo od Googlu.',
        'Samotné rozhraní Search Console ale bývá nepřehledné a data se hůř kombinují s ostatními analýzami. Po propojení s naším nástrojem je vidíte v jednotném prostředí vedle SEO analýz a sledování pozic.',
      ] },
      { h: 'Co po propojení získáte', ul: [
        'Reálné dotazy, na které se web zobrazuje, včetně proklikovosti (CTR).',
        'Prokliky, zobrazení a průměrná pozice za zvolené období.',
        'Nejvýkonnější stránky a srovnání vývoje v čase.',
        'Filtrování a vyhledávání napříč dotazy i stránkami.',
        'Export dat do PDF reportu a naplánované reporty e-mailem.',
      ] },
      { h: 'Jak propojení probíhá', p: [
        'Propojení je bezpečné přes oficiální přihlášení Googlem (OAuth) — nikdy nezadáváte heslo k účtu a přístup můžete kdykoli odebrat. Po pár kliknutích vidíte data všech webů, které ve Search Console spravujete.',
        'Funkce je dostupná po přihlášení. Vytvoření účtu je zdarma.',
      ] },
    ],
    ...CTA_REGISTER,
  },
  {
    slug: 'optimalizace-obsahu',
    tag: 'Optimalizace obsahu',
    title: 'Content Optimizer — obsah, který předčí konkurenci',
    subtitle: 'Zadejte klíčové slovo a svou stránku. Nástroj rozebere top výsledky vyhledávání a ukáže, jaká témata a výrazy vám v obsahu chybí.',
    sections: [
      { h: 'Od domněnek ke konkrétním datům', p: [
        'Psát obsah „od oka" už dnes nestačí. Stránky na předních pozicích mají něco společného — pokrývají téma do určité hloubky a používají sadu souvisejících výrazů, které vyhledávač očekává. Content Optimizer tyto vzorce odhalí a převede je na konkrétní seznam doporučení pro váš text.',
        'Místo hádání, o čem ještě psát, dostanete podloženou odpověď založenou na tom, co reálně funguje u konkurence na první stránce.',
      ] },
      { h: 'Co nástroj udělá', ul: [
        'Načte a rozebere top výsledky vyhledávání pro vaše klíčové slovo.',
        'Najde výrazy a témata, která konkurence pokrývá a vám chybí.',
        'Doporučí cílovou délku textu a strukturu nadpisů.',
        'Zohlední otázky a podtémata, na která se lidé ptají.',
        'Uloží historii optimalizací, ať se k výsledkům můžete vracet.',
      ] },
      { h: 'Pro koho je určený', p: [
        'Content Optimizer ocení každý, kdo tvoří obsah s cílem umístit se ve vyhledávání — od redaktorů a copywriterů po majitele e-shopů a agentury. V kombinaci s analýzou obsahu a sledováním pozic získáte ucelený postup od plánu textu po měření jeho výsledků.',
        'Funkce vyžaduje přihlášení (využívá AI a ukládá historii). Účet si vytvoříte zdarma.',
      ] },
    ],
    ...CTA_REGISTER,
  },
  {
    slug: 'monitoring-webu',
    tag: 'Monitoring webů',
    title: 'Monitoring a statistiky webů',
    subtitle: 'Spravujte všechny své weby na jednom místě, sledujte jejich vývoj v čase a mějte přehled o skóre, pozicích i návštěvnosti bez ručního přepínání.',
    sections: [
      { h: 'Přehled o všech webech naráz', p: [
        'Pokud se staráte o víc než jeden web — ať jako majitel, marketér nebo agentura — rychle ztrácíte přehled, na čem zrovna stojí který projekt. Monitoring webů to řeší tím, že všechny vaše domény drží na jednom místě a sám sleduje jejich vývoj.',
        'Nemusíte ručně spouštět analýzy a porovnávat čísla v hlavě. Dashboard vám ukáže stav každého webu a upozorní, kde se něco zhoršilo.',
      ] },
      { h: 'Co sledujeme', ul: [
        'Celkové SEO skóre webu a jeho vývoj v čase.',
        'Pozice klíčových slov a jejich změny.',
        'Data ze Search Console (po propojení) — prokliky, zobrazení, dotazy.',
        'Výsledky crawlů a nově vzniklé chyby.',
        'Přehledný profil každého webu na samostatné stránce statistik.',
      ] },
      { h: 'Jak to využijete', p: [
        'Každý web má svou stránku se statistikami, kde vidíte všechna data pohromadě — ideální podklad pro report klientovi i pro vlastní rozhodování, kam napřít energii. Počet sledovaných webů se řídí vaším plánem.',
        'Funkce je dostupná po přihlášení. Vytvoření účtu je zdarma a weby přidáte hned po registraci.',
      ] },
    ],
    ...CTA_REGISTER,
  },
  {
    slug: 'historie',
    tag: 'Historie & vývoj',
    title: 'Historie analýz a sledování v čase',
    subtitle: 'Všechny vaše analýzy, crawly a měření přehledně na jednom místě — sledujte, jak se SEO vašich webů vyvíjí týden po týdnu.',
    sections: [
      { h: 'Proč sledovat vývoj', p: [
        'Jednorázová analýza řekne, jak je web na tom dnes. Skutečnou hodnotu ale získáte, až čísla začnete sledovat v čase — uvidíte, jestli vaše úpravy zabraly, kdy se něco zhoršilo a které weby potřebují pozornost.',
        'Historie z každé analýzy dělá bod na křivce. Místo izolovaných výsledků máte trend, ze kterého se dá rozhodovat.',
      ] },
      { h: 'Co najdete v historii', ul: [
        'Všechny provedené analýzy a jejich skóre seřazené v čase.',
        'Vývoj skóre jednotlivých webů v přehledných grafech.',
        'Výsledky crawlů a srovnání mezi jednotlivými běhy.',
        'Měření pozic klíčových slov a jejich změny.',
        'Rychlý přístup k libovolnému staršímu výsledku bez opakované analýzy.',
      ] },
      { h: 'Jak to využijete', p: [
        'Historie je ideální podklad pro report klientovi i pro vlastní rozhodování — na jednom místě vidíte, co se za dané období povedlo. Data se ukládají automaticky po každé analýze, takže se nemusíte o nic starat.',
        'Funkce je dostupná po přihlášení. Vytvoření účtu je zdarma.',
      ] },
    ],
    ...CTA_REGISTER,
  },
  {
    slug: 'pdf-reporty',
    tag: 'PDF reporty',
    title: 'PDF reporty a e-mailové přehledy',
    subtitle: 'Vygenerujte brandovaný PDF report z analýzy, crawlu nebo pozic a nechte si posílat pravidelné přehledy e-mailem — ideální pro klienty i vedení.',
    sections: [
      { h: 'Od dat k prezentovatelnému výstupu', p: [
        'Surová data jsou užitečná pro práci, ale klient nebo nadřízený ocení přehledný dokument. PDF report shrne výsledky analýzy do úhledné podoby, kterou stačí odeslat — bez ručního přepisování čísel do prezentace.',
        'Report obsahuje skóre, jednotlivé kontroly i doporučení, takže příjemce hned vidí stav webu i co s ním dál.',
      ] },
      { h: 'Co reporty nabízejí', ul: [
        'Export analýzy, crawlu i sledování pozic do PDF.',
        'Přehledné shrnutí skóre a jednotlivých kontrol s doporučeními.',
        'Pravidelné e-mailové přehledy (denně, týdně, měsíčně) podle plánu.',
        'Vhodné pro reporting klientům i interní přehled.',
      ] },
      { h: 'Jak na to', p: [
        'Report vygenerujete jedním kliknutím z výsledku analýzy nebo crawlu. Pravidelné e-mailové přehledy si zapnete u webu nebo projektu a dál už chodí automaticky.',
        'Funkce je dostupná po přihlášení. Vytvoření účtu je zdarma.',
      ] },
    ],
    ...CTA_REGISTER,
  },
]
